import { AnexApiClient } from '@anex/common'
import { makeAutoObservable, reaction } from 'mobx'

import { RootStore } from './RootStore'
import { TokenManager } from '~/utils/TokenManager'
import { authKey, Constants } from '~/utils/Constants'
import { AccountStore } from './AccountStore'
import { IAddressFieldsLookup } from '~/types'

/**
 * App store should have the auth details, lookups or other methods used in the app
 * In the sequence of initializing the mobx store, this will be in the last
 * for we need data from other stores to be available for use (NOTE: might need to make a work around for this)
 */
export class AppStore {
  api: AnexApiClient
  account: AccountStore
  isAuthenticated = false
  token = ''
  isMfaDone = false
  addressFieldsLookup: IAddressFieldsLookup = {
    unitTypes: undefined,
    streetTypes: undefined,
    states: undefined,
    levelTypes: undefined,
  }

  constructor(rootStore: RootStore) {
    this.api = rootStore.api
    this.account = rootStore.account

    makeAutoObservable(this)
    // Load the MFA status from session storage
    this.loadFromSessionStorage()

    reaction(
      () => this.isMfaDone,
      (isMfaDone) => {
        sessionStorage.setItem('isMfaDone', isMfaDone.toString())
      }
    )
  }

  *setMfaDone() {
    this.isMfaDone = yield true
  }

  *setAuthenticatedDetails(token: string, isAuthenticated: boolean) {
    this.token = yield token
    this.isAuthenticated = yield isAuthenticated
  }

  *loadFromSessionStorage() {
    const mfaStorage = sessionStorage.getItem('isMfaDone')
    //Always set mfa true for Origin ANEXDEV-12337
    if (Constants.WHOLESALERCODE === 'OE') {
      this.setMfaDone()
    } else if (mfaStorage) {
      this.isMfaDone = yield JSON.parse(mfaStorage)
    }
  }

  *setAddressLookupFields(fields: IAddressFieldsLookup) {
    this.addressFieldsLookup = yield fields
  }

  get getAddressFieldsLookup() {
    return this.addressFieldsLookup
  }

  *setToken(token: string) {
    yield TokenManager.setToken(token)
  }

  /**
   * Get Authentication Token via Email
   */
  getAuthenticated(email: string) {
    return this.api.V1.Authentication.AuthenticateViaEmail(
      {
        email: email,
        wid: WHOLESALERCODE,
      },
      TokenManager.getTokenHeader()
    )
      .then((r: any) => {
        console.log(r)
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Refresh Token
   * @param passedToken token to be refresh
   */
  async refreshToken(passedToken: string) {
    if (!passedToken) {
      alert(
        'Your session has expired. You will be redirected to the login page.'
      )
      window.location.replace('/login')
    }

    try {
      var newToken = await this.api.V1.Authentication.RefreshToken({
        headers: {
          'X-Access-Token': passedToken,
        },
      })

      if (newToken && newToken.token) {
        this.setToken(newToken.token) // this will also save it on session storage

        this.setAuthenticatedDetails(newToken.token, true)

        // Auto Refresh after a set timeout
        this.autoRefreshToken()
      }
    } catch (error) {
      // TODO: Do we need to log out or show a modal saying user session has expired?
      //       and record the error if necessarily?
      console.info('Token Error', error)
      TokenManager.clearToken()
      this.setAuthenticatedDetails('', false)
      // return to
      const returnTo = sessionStorage.getItem('anex-oss-rto') || '/login'
      window.location.replace(returnTo)
    }
  }

  /**
   * Auto Refresh Token
   */
  autoRefreshToken(): any {
    window.setTimeout(
      () => {
        this.refreshToken(TokenManager.getToken())
      },
      10 * 60 * 1000
    )
  }

  /**
   * Get OSS Token
   * This is mostly used to download the invoice PDF file
   */
  async getOssToken() {
    const payload = {
      accountCode: this.account.profile?.accountCode,
      singleUse: true,
    }

    return await this.api.V1.Authentication.GetOssToken(
      payload,
      TokenManager.getTokenHeader()
    )
      .then((r) => (r.token ? r.token : false))
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Download a PDF Invoice
   */
  async downloadPdfInvoice(invoiceCode: string) {
    const token = await this.getOssToken()
    if (token) {
      const a = document.createElement('a')
      a.setAttribute(
        'href',
        `${Constants.ANEX_API_ENDPOINT}/api/v1/account/invoices/full/pdf/${invoiceCode}?download=true&jwt=${token}`
      )
      a.setAttribute('download', `invoice-${invoiceCode}.pdf`)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      return true
    } else {
      return false
    }
  }

  /**
   * Log Out
   */
  logout() {
    const returnTo = sessionStorage.getItem('anex-oss-rto') || '/'
    sessionStorage.removeItem(authKey)
    sessionStorage.removeItem('anex-oss-rto') // return to url
    TokenManager.clearToken()
    window.location.replace(returnTo)
  }

  /**
   * Address Fields Lookup
   */
  async getAddressFieldsLookupData() {
    const unitTypes = await this.api.V1.Lookup.Address.UnitTypes(
      TokenManager.getTokenHeader()
    )
    const levelTypes = await this.api.V1.Lookup.Address.LevelTypes(
      TokenManager.getTokenHeader()
    )
    const streetTypes = await this.api.V1.Lookup.Address.StreetTypes(
      TokenManager.getTokenHeader()
    )
    const states = await this.api.V1.Lookup.Address.LocationStates(
      TokenManager.getTokenHeader()
    )

    await this.setAddressLookupFields({
      unitTypes: unitTypes.unitTypes,
      levelTypes: levelTypes.levelTypes,
      streetTypes: streetTypes.streetTypes,
      states: states.locationStates,
    } as IAddressFieldsLookup)
  }

  /**
   * Request MFA
   */
  requestMfaCode(mfaType: string, contextId: string) {
    const profile = this.account.getProfile

    const payload = {
      contextId: contextId,
      accountCode: profile.accountCode,
      ...(mfaType === 'sms'
        ? { portInMsn: profile.contacts[0].phoneMobile }
        : { emailAddress: profile.email }),
    }

    return mfaType === 'sms'
      ? this.api.V1.Mobile.InitiatePortInVerification(
          payload,
          TokenManager.getTokenHeader()
        ).then((r) => r)
      : this.api.V1.Mobile.InitiateEmailVerification(
          payload,
          TokenManager.getTokenHeader()
        ).then((r) => r)
  }

  verifyMfaCode(code: string, mfaType: string, contextId: string) {
    const profile = this.account.getProfile

    let payload: any = {
      contextId: contextId,
      ...(mfaType === 'sms'
        ? { portInMsn: profile.contacts[0].phoneMobile }
        : { emailAddress: profile.email }),
      verificationCode: code,
    }

    return mfaType === 'sms'
      ? this.api.V1.Mobile.ValidatePortInVerification(
          payload,
          TokenManager.getTokenHeader()
        ).then((r) => r)
      : this.api.V1.Mobile.HandleEmailVerification(
          payload,
          TokenManager.getTokenHeader()
        ).then((r) => r)
  }
}
